/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

export function useHasChanges<T extends Record<string, unknown>>(
  currentValues: T,
  initialValues: T
): boolean {
  const arePermissionsEqual = (
    a: { name: string; checked?: string[] }[],
    b: { name: string; checked?: string[] }[]
  ) => {
    if (a.length !== b.length) {
      return false;
    }

    const mapB = new Map(
      b.map((item) => [
        item.name,
        new Set((item.checked ?? []).filter(Boolean)),
      ])
    );

    return a.every(({ name, checked }) => {
      const setB = mapB.get(name);
      if (!setB) {
        return false;
      }

      const filtered = (checked ?? []).filter(Boolean);
      if (filtered.length !== setB.size) {
        return false;
      }

      return filtered.every((val) => setB.has(val));
    });
  };

  return Object.keys(initialValues).some((key) => {
    const current = currentValues[key];
    const initial = initialValues[key];

    if (Array.isArray(current) && Array.isArray(initial)) {
      // Detect permissions array
      if (
        current.length > 0 &&
        typeof current[0] === "object" &&
        "name" in current[0] &&
        "checked" in current[0]
      ) {
        return !arePermissionsEqual(
          current as { name: string; checked?: string[] }[],
          initial as { name: string; checked?: string[] }[]
        );
      }

      // Generic array comparison
      if (current.length !== initial.length) {
        return true;
      }
      return (
        current.some((v) => !initial.includes(v)) ||
        initial.some((v) => !current.includes(v))
      );
    }

    return current !== initial;
  });
}
